import type { ChatChannelResponse } from './api';

/**
 * Cross-arche chat display helpers.
 *
 * Lives in `lib/` (like sanitize-chat-html.ts) rather than inside the chat
 * arche because other arches that integrate with chat through its public API —
 * e.g. customer-support's "send ticket to chat" — need the same channel
 * labelling. Arches must never import from a sibling arche's directory, so the
 * shared formatting lives here and the chat arche re-exports it.
 */

/**
 * Display label for a self-DM ("Saved Messages") — a direct channel whose only
 * member is the current user, used for noting things to yourself or relaying a
 * message between your own devices. Mirrors Telegram's "Saved Messages".
 */
export const SELF_DM_LABEL = 'Saved Messages';

/** Shown for a group/guild/community channel with no name. */
export const UNNAMED_CHANNEL_LABEL = 'Unnamed Channel';

/**
 * The name shown for a channel in the list and previews. Self-DMs read as
 * "Saved Messages"; DMs prefer the partner's display name (falling back through
 * the channel name and partner id); everything else uses the channel name.
 * Single source of truth so the list item and the message preview never drift.
 */
export function channelDisplayName(channel: ChatChannelResponse): string {
  if (channel.isSelfDm === true) return SELF_DM_LABEL;
  if (channel.channelType === 'direct') {
    return channel.dmPartnerDisplayName || channel.name || channel.dmPartnerId || 'Direct Message';
  }
  return channel.name || UNNAMED_CHANNEL_LABEL;
}
